import { useEffect, useState } from 'react';
import { ProcessingFeeOverride, ProcessingFeeOverrideConf, User } from '../../../clients/quipli-service/user/user';
import { Button } from '../../../components/form/button';
import { Modal } from '../../../components/modal/modal';
import { Spinner } from '../../../components/spinner/spinner';
import { Field } from '../../../components/field/field';
import { toast } from '../../../util/toasts';
import { useUserAPI } from '../../../clients/quipli-service/user/user-api';
import { Typography } from '../../../components/typography/typography';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { UpsertRepresentativeRequest } from '../../../clients/quipli-service/representatives/representative';
import { ButtonGroup } from '../../../components/button-group/button-group';
import { Phone } from '../../../components/field/phone';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { LicenseScanFormV2 } from './license-scan-form-v2';
import { LicenseScanFormV1 } from './license-scan-form-v1';

export const CreateUserModal = ({
  isOpen,
  disableScanning = false,
  closeModal,
  setNewUser,
}: {
  isOpen: boolean;
  disableScanning?: boolean;
  closeModal: () => any;
  setNewUser?: (u: User) => void;
}) => {
  const { getMultipleConfigurations } = useAPI();

  const [formType, setFormType] = useState<'scan' | 'manual' | undefined>();
  const [enableLicenseScanning, setEnableLicenseScanning] = useState<boolean>(false);
  const [licenseScanFormVersion, setLicenseScanFormVersion] = useState<number>(1);
  const [user, setUser] = useState<User>();
  const [modalTitle, setModalTitle] = useState<string>('Add New Customer');

  useEffect(() => {
    getMultipleConfigurations(['CONF_ENABLE_HARDWARE_LICENSE_SCANNING', 'CONF_IDSCAN_PROFILE_VERSION'])
      .then((configs) => {
        const enableHardwareLicenseScanning =
          configs.find((c) => c.variable === 'CONF_ENABLE_HARDWARE_LICENSE_SCANNING')?.value === '1' &&
          !disableScanning;
        setEnableLicenseScanning(enableHardwareLicenseScanning);
        if (!enableHardwareLicenseScanning) {
          setFormType('manual');
        }
        const idScanProfileVersion = configs.find((c) => c.variable === 'CONF_IDSCAN_PROFILE_VERSION')?.value || '1';
        setLicenseScanFormVersion(parseInt(idScanProfileVersion));
      })
      .catch(() => toast('Failed to fetch configuration', 'error'));
  }, []);

  const onUserScanSuccessful = (user: User) => {
    setUser(user);
    onSetFormType('manual');
  };

  const onUserScanFailed = () => {
    onSetFormType('manual');
  };

  const onCloseModal = () => {
    setFormType(enableLicenseScanning ? undefined : 'manual');

    if (user && setNewUser) {
      setNewUser(user);
    }

    setUser(undefined);
    closeModal();
  };

  const onSetFormType = (type: 'scan' | 'manual') => {
    if (type === 'scan') {
      setModalTitle("Scan Driver's License");
    } else if (formType === 'scan' && type === 'manual') {
      setModalTitle('Update Contact Information');
    } else {
      setModalTitle('Add New Customer');
    }

    setFormType(type);
  };

  return (
    <Modal isOpen={isOpen} title={modalTitle} close={onCloseModal}>
      {enableLicenseScanning && formType === undefined && (
        <div className="ml-auto mr-auto">
          <div className="mt-2 mb-2">
            <Button className="button button-primary w-full" onClick={() => onSetFormType('scan')}>
              <Typography type={'text-md'} className={'gap-[6px] py-[6px]'}>
                Scan From Driver's License
              </Typography>
            </Button>
          </div>

          <div className="mt-2 mb-2">
            <Button className="button button-primary w-full" onClick={() => onSetFormType('manual')}>
              <Typography type={'text-md'} className={'gap-[6px] py-[6px]'}>
                Manually Enter Customer Info
              </Typography>
            </Button>
          </div>
        </div>
      )}

      {formType === 'manual' && <ManualUserForm user={user} closeModal={onCloseModal} setNewUser={setNewUser} />}

      {formType === 'scan' &&
        (licenseScanFormVersion === 2 ? (
          <LicenseScanFormV2
            userScanSuccessful={onUserScanSuccessful}
            userScanFailed={onUserScanFailed}
            closeModal={onCloseModal}
            user={user}
          />
        ) : (
          <LicenseScanFormV1
            userScanSuccessful={onUserScanSuccessful}
            userScanFailed={onUserScanFailed}
            closeModal={onCloseModal}
            user={user}
          />
        ))}
    </Modal>
  );
};

const ManualUserForm = ({
  user,
  closeModal,
  setNewUser,
}: {
  user?: User;
  closeModal: () => void;
  setNewUser?: (u: User) => void;
}) => {
  const { getConfiguration } = useAPI();
  const { createUser, updateUser } = useUserAPI();

  const [name, setName] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [phoneIsValid, setPhoneIsValid] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [processingFeeOverrideConf, setProcessingFeeOverrideConf] = useState<ProcessingFeeOverrideConf>();
  const [processingFeeOverride, setProcessingFeeOverride] = useState<ProcessingFeeOverride>();
  const [withoutEmailChecked, setWithoutEmailChecked] = useState<boolean>(false);
  const [userType, setUserType] = useState<string>(user?.structureType ?? 'user');
  const [representativeName, setRepresentativeName] = useState<string>();
  const [representativePhone, setRepresentativePhone] = useState<string>('');
  const [representativePhoneIsValid, setRepresentativePhoneIsValid] = useState<boolean>(false);
  const [representativeTitle, setRepresentativeTitle] = useState<string>();
  const [representativeEmail, setRepresentativeEmail] = useState<string>();

  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isRequestInProgress, setIsRequestInProgress] = useState<boolean>(false);

  const clearValues = () => {
    setName('');
    setCompany('');
    setPhone('');
    setEmail('');
    setWithoutEmailChecked(false);
    setUserType('user');
    setProcessingFeeOverride(undefined);
    closeModal();
  };

  const onSubmit = async () => {
    if (isDisabled) return;

    if (phone !== '' && !phoneIsValid) {
      toast('Please enter a valid phone number', 'error');
      return;
    }

    if (representativePhone !== '' && !representativePhoneIsValid) {
      toast('Please enter a valid representative phone number', 'error');
      return;
    }

    setIsRequestInProgress(true);
    try {
      const representatives: Omit<UpsertRepresentativeRequest, 'userId'>[] | undefined =
        userType == 'company' && representativeName
          ? [
              {
                name: representativeName,
                phone: representativePhone,
                title: representativeTitle,
                email:
                  representativeEmail && representativeEmail.length > 0 ? representativeEmail : 'customer@quipli.com',
              },
            ]
          : undefined;
      const newUser = await (user
        ? updateUser({ ...user, name, email: email.trim(), company, phone, processingFeeOverride })
        : createUser(
            name,
            withoutEmailChecked,
            userType,
            email.trim(),
            company,
            phone,
            representatives,
            processingFeeOverride,
          ));
      setNewUser && setNewUser(newUser);
      setIsRequestInProgress(false);
      onClose();
    } catch (e: any) {
      setIsRequestInProgress(false);
      if (e.message.msg) {
        toast(e.message.msg, 'error');
      } else if (e.message) {
        toast(e.message, 'error');
      }
    }
  };

  const onClose = () => {
    user ? closeModal() : clearValues();
  };

  useEffect(() => {
    getConfiguration('CONF_PROCESSING_FEE_OVERRIDE')
      .then((config) => {
        setProcessingFeeOverrideConf(config?.value as ProcessingFeeOverrideConf);
      })
      .catch(() => toast('Failed to fetch configuration', 'error'));
  }, []);

  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
      setCompany(user.company);
      setPhone(user.phone);
      setWithoutEmailChecked(false);
    }
  }, [user]);

  useEffect(() => {
    setIsDisabled(name.length === 0 || (!withoutEmailChecked && email.length === 0));
  }, [name, withoutEmailChecked, email]);

  const buttonItems: { label: string; value: 'user' | 'company' }[] = [
    { label: 'User', value: 'user' },
    { label: 'Company', value: 'company' },
  ];

  return (
    <div>
      <ButtonGroup<'user' | 'company'>
        items={buttonItems}
        onClick={(value) => setUserType(value as 'user' | 'company')}
      ></ButtonGroup>

      <Field
        label={`${userType == 'user' ? 'Full name' : 'Company name'}`}
        className="mt-2 mb-2"
        dataCy="full_name"
        type="text"
        value={name}
        onChange={setName}
        isRequired
      />

      {userType == 'user' && (
        <Field
          label="Company name"
          className="mb-2"
          dataCy="company_name"
          type="text"
          value={company}
          onChange={setCompany}
        />
      )}

      <Phone
        label="Phone number"
        className="mb-2"
        dataCy="phone_number"
        value={phone}
        onChange={setPhone}
        onChangeValidity={setPhoneIsValid}
      />

      <div className="flex flex-col gap-[6px]">
        <Field
          label="Email address"
          className="mb-2"
          dataCy="email_address"
          type="text"
          value={email}
          onChange={setEmail}
          isRequired={!withoutEmailChecked}
        />

        {!user && (
          <Checkbox
            type="white"
            className="mb-2"
            label={<span className="!text-grey-700">Does not have an email</span>}
            onChange={setWithoutEmailChecked}
            isChecked={withoutEmailChecked}
            dataCy="without_email"
          />
        )}
      </div>

      {!processingFeeOverrideConf ||
        (processingFeeOverrideConf !== ProcessingFeeOverrideConf.ApplyFee && (
          <Checkbox
            type="white"
            className="mb-2"
            label={
              <span className="!text-grey-700">
                {processingFeeOverrideConf == ProcessingFeeOverrideConf.OptionallyApplyFee
                  ? 'Waive Processing Fee'
                  : 'Apply Processing Fee'}
              </span>
            }
            onChange={(isChecked) => {
              isChecked
                ? setProcessingFeeOverride(
                    processingFeeOverrideConf == ProcessingFeeOverrideConf.OptionallyApplyFee
                      ? ProcessingFeeOverride.WaiveFee
                      : ProcessingFeeOverride.ApplyFee,
                  )
                : setProcessingFeeOverride(undefined);
            }}
            isChecked={
              !processingFeeOverride
                ? false
                : processingFeeOverrideConf === ProcessingFeeOverrideConf.OptionallyApplyFee
                ? processingFeeOverride == ProcessingFeeOverride.WaiveFee
                : processingFeeOverride == ProcessingFeeOverride.ApplyFee
            }
            dataCy="processing_fee_override"
          />
        ))}

      {userType == 'company' && !user && (
        <>
          <div className="mt-2 mb-2">
            <Typography type={'text-md'} className={'gap-[6px] py-[6px]'}>
              Primary Representative
            </Typography>
          </div>

          <Field
            type="text"
            label="Full name"
            className="mb-2"
            dataCy="full_name"
            value={representativeName}
            onChange={(name: string) => {
              setRepresentativeName(name);
            }}
            isRequired={true}
          />
          <Phone
            label="Phone number"
            className="mb-2"
            dataCy="phone_number"
            value={phone}
            onChange={setRepresentativePhone}
            onChangeValidity={setRepresentativePhoneIsValid}
          />
          <Field
            type="text"
            label="Title"
            className="mb-2"
            dataCy="representative_title"
            value={representativeTitle}
            onChange={(title: string) => {
              setRepresentativeTitle(title);
            }}
            isRequired={false}
          />

          <Field
            type="text"
            label="Email"
            className="mb-2"
            value={representativeEmail}
            onChange={(email: string) => {
              setRepresentativeEmail(email);
            }}
            isRequired={false}
            dataCy="representative_email"
          />
        </>
      )}

      {isRequestInProgress ? (
        <Spinner />
      ) : (
        <div className="flex flex-col lg:flex-row [&>*]:!grow gap-3 mt-4">
          <Button className="button button-secondary" onClick={() => closeModal()}>
            Cancel
          </Button>

          <Button
            className="button button-primary"
            id="submit"
            disabled={isDisabled || isRequestInProgress}
            onClick={onSubmit}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
};
